import type { Category, MenuItem, Order } from '../types';

export interface RankedItem {
  item: MenuItem;
  orders: number;
}

export interface CategoryTotal {
  cat: Category;
  orders: number;
  itemCount: number;
}

/**
 * Units sold per item, derived from the order history rather than kept as its
 * own counter — otherwise an order pulled from another device would never show
 * up in the rankings.
 */
export function countsFromOrders(orders: Order[]): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const o of orders) {
    for (const line of o.items) {
      counts[line.itemId] = (counts[line.itemId] ?? 0) + line.qty;
    }
  }
  return counts;
}

/**
 * Items sorted by units sold, descending. The menu is passed in rather than
 * imported: it lives in the database now and changes at runtime.
 */
export function rankItems(
  items: MenuItem[],
  orderCounts: Record<string, number>,
  onlySold = false,
): RankedItem[] {
  const ranked = items
    .map((item) => ({ item, orders: orderCounts[item.id] ?? 0 }))
    .sort((a, b) => b.orders - a.orders);
  return onlySold ? ranked.filter((r) => r.orders > 0) : ranked;
}

/** Largest units-sold figure, floored at 1 so bar widths never divide by zero. */
export function maxItemOrders(
  items: MenuItem[],
  orderCounts: Record<string, number>,
): number {
  return Math.max(1, ...items.map((i) => orderCounts[i.id] ?? 0));
}

export function categoryTotals(
  categories: Category[],
  items: MenuItem[],
  orderCounts: Record<string, number>,
): CategoryTotal[] {
  return categories
    .map((cat) => {
      const own = items.filter((i) => i.cat === cat.id);
      return {
        cat,
        orders: own.reduce((s, i) => s + (orderCounts[i.id] ?? 0), 0),
        itemCount: own.length,
      };
    })
    .sort((a, b) => b.orders - a.orders);
}

export function maxCategoryOrders(totals: CategoryTotal[]): number {
  return Math.max(1, ...totals.map((c) => c.orders));
}

/** 0-1 bar fill fraction, safe against an empty session. */
export function barFraction(value: number, max: number): number {
  return max > 0 ? value / max : 0;
}

/* ---------------------------------------------------------------------------
 * Time periods
 *
 * The analytics screen answers "how are we doing today / this week / this
 * month", so everything below works on calendar periods in the device's own
 * timezone. Dates are handled with local getters rather than ISO strings: a
 * till in India must bucket an 11pm order into today, not tomorrow UTC.
 * ------------------------------------------------------------------------- */

export type Period = 'day' | 'week' | 'month' | 'custom';

/** Inclusive start and end dates for the custom period, as YYYY-MM-DD. */
export interface DateRange {
  start: string;
  end: string;
}

export interface Bucket {
  /** Axis label, already short enough to render under a bar. */
  label: string;
  from: number;
  to: number;
  orders: number;
  revenue: number;
  items: number;
}

export interface PeriodSummary {
  orders: number;
  revenue: number;
  items: number;
  /** Mean order value, 0 when nothing was sold. */
  average: number;
}

export function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/** Monday, matching how a restaurant week is usually counted here. */
export function startOfWeek(d: Date): Date {
  const day = startOfDay(d);
  // getDay() is 0 for Sunday, so shift it to a Monday-first index.
  const offset = (day.getDay() + 6) % 7;
  day.setDate(day.getDate() - offset);
  return day;
}

export function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

/** "2026-09-06" for a date, in local time — never an ISO UTC string. */
export function toDateInput(d: Date): string {
  return [
    d.getFullYear(),
    String(d.getMonth() + 1).padStart(2, '0'),
    String(d.getDate()).padStart(2, '0'),
  ].join('-');
}

/** Parses YYYY-MM-DD as local midnight. Null when it is not a real date. */
export function fromDateInput(value: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
  if (!m) return null;
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const date = new Date(y, mo - 1, d);
  // Rejects 2026-02-31, which Date would roll forward into March.
  if (date.getFullYear() !== y || date.getMonth() !== mo - 1 || date.getDate() !== d) {
    return null;
  }
  return date;
}

/**
 * The half-open range [from, to) covered by a period containing `now`.
 *
 * A custom range is inclusive of both dates the user typed, so `to` is midnight
 * at the start of the day *after* the end date — otherwise an order taken on
 * the last afternoon would fall outside the range they asked for.
 */
export function periodRange(
  period: Period,
  now: Date = new Date(),
  custom?: DateRange,
): { from: number; to: number } {
  if (period === 'custom') {
    const a = custom ? fromDateInput(custom.start) : null;
    const b = custom ? fromDateInput(custom.end) : null;
    if (a && b) {
      // Typed backwards is a slip, not an empty result.
      const [lo, hi] = a <= b ? [a, b] : [b, a];
      const end = new Date(hi);
      end.setDate(end.getDate() + 1);
      return { from: lo.getTime(), to: end.getTime() };
    }
    // An incomplete range falls back to today rather than showing everything.
    return periodRange('day', now);
  }
  if (period === 'day') {
    const from = startOfDay(now);
    const to = new Date(from);
    to.setDate(to.getDate() + 1);
    return { from: from.getTime(), to: to.getTime() };
  }
  if (period === 'week') {
    const from = startOfWeek(now);
    const to = new Date(from);
    to.setDate(to.getDate() + 7);
    return { from: from.getTime(), to: to.getTime() };
  }
  const from = startOfMonth(now);
  const to = new Date(from);
  to.setMonth(to.getMonth() + 1);
  return { from: from.getTime(), to: to.getTime() };
}

export function ordersBetween(orders: Order[], from: number, to: number): Order[] {
  return orders.filter((o) => o.placedAt >= from && o.placedAt < to);
}

export function summarise(orders: Order[]): PeriodSummary {
  const revenue = orders.reduce((s, o) => s + o.total, 0);
  const items = orders.reduce((s, o) => s + o.qty, 0);
  return {
    orders: orders.length,
    revenue,
    items,
    average: orders.length ? revenue / orders.length : 0,
  };
}

const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/**
 * The empty bars for one period, before anything is counted into them.
 *
 * A day is split by hour, because "when are we busy" is the question a day view
 * is actually asked. A week is split by day, a month by date. Empty buckets are
 * kept: a gap in trade is information, and dropping them would make the axis
 * lie about the spacing.
 *
 * Separate from `buildSeries` so that money out buckets by exactly the same
 * rules as money in — the Expenses chart sits under the same period tabs, and
 * two copies of this date arithmetic would drift apart the first time one of
 * them was fixed.
 */
export function emptyBuckets(
  period: Period,
  now: Date = new Date(),
  custom?: DateRange,
): Bucket[] {
  const { from, to } = periodRange(period, now, custom);
  const start = new Date(from);
  const buckets: Bucket[] = [];

  if (period === 'custom') {
    const days = Math.max(1, Math.round((to - from) / 86400000));
    // Beyond a couple of months a bar per day is unreadable even scrolled, so
    // the range switches to a bar per week.
    const step = days > 62 ? 7 : 1;
    for (let i = 0; i < days; i += step) {
      const a = new Date(start);
      a.setDate(start.getDate() + i);
      const b = new Date(start);
      b.setDate(start.getDate() + Math.min(i + step, days));
      const count = Math.ceil(days / step);
      buckets.push({
        label:
          i === 0 || i + step >= days || Math.floor(i / step) % Math.ceil(count / 8) === 0
            ? `${a.getDate()}/${a.getMonth() + 1}`
            : '',
        from: a.getTime(),
        to: b.getTime(),
        orders: 0,
        revenue: 0,
        items: 0,
      });
    }
  } else if (period === 'day') {
    for (let h = 0; h < 24; h++) {
      const a = new Date(start);
      a.setHours(h);
      const b = new Date(a);
      b.setHours(h + 1);
      // 12a, 3a, 6a … keeps the axis readable without a label on every bar.
      const h12 = h % 12 === 0 ? 12 : h % 12;
      buckets.push({
        label: h % 3 === 0 ? `${h12}${h < 12 ? 'a' : 'p'}` : '',
        from: a.getTime(),
        to: b.getTime(),
        orders: 0,
        revenue: 0,
        items: 0,
      });
    }
  } else {
    const days = period === 'week' ? 7 : daysInMonth(start);
    for (let i = 0; i < days; i++) {
      const a = new Date(start);
      a.setDate(start.getDate() + i);
      const b = new Date(a);
      b.setDate(a.getDate() + 1);
      buckets.push({
        label:
          period === 'week'
            ? DAY_NAMES[i]
            : // A label on every date is unreadable on a phone; every third is enough.
              i % 3 === 0 || i === days - 1
              ? String(a.getDate())
              : '',
        from: a.getTime(),
        to: b.getTime(),
        orders: 0,
        revenue: 0,
        items: 0,
      });
    }
  }

  return buckets;
}

/**
 * The bars for one period, with the orders counted into them.
 */
export function buildSeries(
  orders: Order[],
  period: Period,
  now: Date = new Date(),
  custom?: DateRange,
): Bucket[] {
  const buckets = emptyBuckets(period, now, custom);

  for (const o of orders) {
    // Buckets are contiguous and equal-width, so the index is arithmetic — a
    // scan per order would be O(orders × buckets) on a busy month.
    const idx = bucketIndexFor(o.placedAt, buckets);
    if (idx < 0) continue;
    const b = buckets[idx];
    b.orders += 1;
    b.revenue += o.total;
    b.items += o.qty;
  }

  return buckets;
}

function daysInMonth(start: Date): number {
  return new Date(start.getFullYear(), start.getMonth() + 1, 0).getDate();
}

/** Which bucket an instant falls in, or -1 when it is outside the period. */
export function bucketIndexFor(at: number, buckets: Bucket[]): number {
  if (!buckets.length || at < buckets[0].from) return -1;
  const last = buckets[buckets.length - 1];
  if (at >= last.to) return -1;
  // Daylight-saving would make a pure division wrong, so narrow then confirm.
  const span = (last.to - buckets[0].from) / buckets.length;
  let i = Math.min(buckets.length - 1, Math.floor((at - buckets[0].from) / span));
  while (i > 0 && at < buckets[i].from) i--;
  while (i < buckets.length - 1 && at >= buckets[i].to) i++;
  return at >= buckets[i].from && at < buckets[i].to ? i : -1;
}

/** Largest revenue in a series, floored at 1 so a bar never divides by zero. */
export function maxBucketRevenue(buckets: Bucket[]): number {
  return Math.max(1, ...buckets.map((b) => b.revenue));
}

/* ---------------------------------------------------------------------------
 * Customers
 *
 * There is no customers table: a name and phone are optional fields typed onto
 * an order, so the list is derived from the order history. That keeps it
 * automatically correct — an order pulled from another device or amended later
 * updates the customer without a second write to keep in step.
 * ------------------------------------------------------------------------- */

export interface CustomerRecord {
  /** Phone when there is one, otherwise the lowercased name. */
  key: string;
  name: string;
  phone: string;
  /** How many times they have ordered. */
  visits: number;
  firstAt: number;
  lastAt: number;
  totalSpent: number;
  /** Every order they placed, newest first. */
  orders: Order[];
}

/**
 * Everyone who left a name or a phone number, most recent visit first.
 *
 * Grouped by phone where there is one, because two Rameshes are two customers
 * but the same number typed on two nights is one. A name-only customer is
 * grouped by lowercased name, which will merge two different people with the
 * same name — the alternative is a list full of duplicates, and the phone is
 * what staff are asked for when it matters.
 */
export function customersFromOrders(orders: Order[]): CustomerRecord[] {
  const byKey = new Map<string, CustomerRecord>();

  for (const o of orders) {
    const name = o.customer.name.trim();
    const phone = o.customer.phone.trim();
    // A walk-in who gave nothing is not a customer record, just an order.
    if (!name && !phone) continue;

    const key = phone || name.toLowerCase();
    const found = byKey.get(key);
    if (found) {
      found.visits += 1;
      found.totalSpent += o.total;
      found.firstAt = Math.min(found.firstAt, o.placedAt);
      if (o.placedAt >= found.lastAt) {
        found.lastAt = o.placedAt;
        // The most recent spelling wins: a corrected name should stick.
        if (name) found.name = name;
        if (phone) found.phone = phone;
      }
      found.orders.push(o);
    } else {
      byKey.set(key, {
        key,
        name,
        phone,
        visits: 1,
        firstAt: o.placedAt,
        lastAt: o.placedAt,
        totalSpent: o.total,
        orders: [o],
      });
    }
  }

  const list = [...byKey.values()];
  for (const c of list) c.orders.sort((a, b) => b.placedAt - a.placedAt);
  return list.sort((a, b) => b.lastAt - a.lastAt);
}

/** Filters the customer list by name or phone, for the search box. */
export function searchCustomers(list: CustomerRecord[], query: string): CustomerRecord[] {
  const q = query.trim().toLowerCase();
  if (!q) return list;
  return list.filter(
    (c) => c.name.toLowerCase().includes(q) || c.phone.replace(/\s/g, '').includes(q),
  );
}
